import { useState, useRef } from 'react';
import { t } from '../i18n';

interface Props {
  terms: string[];
  onAdd: (term: string) => void;
  onRemove: (term: string) => void;
  onImport: (terms: string[]) => void;
  onExport: () => void;
  onApply: () => void; // サーバーへ送信
}

export default function GlossaryEditor({ terms, onAdd, onRemove, onImport, onExport, onApply }: Props) {
  const [inputValue, setInputValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    const t = inputValue.trim();
    if (t) {
      onAdd(t);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      try {
        // JSONフォーマット: { "terms": [...] } またはフラットな配列
        const parsed = JSON.parse(text) as unknown;
        if (Array.isArray(parsed)) {
          onImport(parsed as string[]);
        } else if (
          typeof parsed === 'object' &&
          parsed !== null &&
          'terms' in parsed &&
          Array.isArray((parsed as { terms: unknown }).terms)
        ) {
          onImport((parsed as { terms: string[] }).terms);
        } else {
          alert(t('JSONフォーマットが正しくありません。\n形式: { "terms": ["用語1", "用語2"] }', 'Invalid JSON format.\nFormat: { "terms": ["term1", "term2"] }'));
        }
      } catch {
        // CSVとして処理（1行1用語）
        const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
        onImport(lines);
      }
    };
    reader.readAsText(file, 'UTF-8');

    // 同じファイルを再度選べるようリセット
    e.target.value = '';
  };

  return (
    <div className="card">
      <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{t(`用語集 (${terms.length}件)`, `Glossary (${terms.length})`)}</span>
        <div className="btn-group">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => fileInputRef.current?.click()}
            title={t('JSONまたはCSVからインポート', 'Import from JSON or CSV')}
          >
            📥 Import
          </button>
          <button
            className="btn btn-ghost btn-sm"
            onClick={onExport}
            title={t('JSONとしてエクスポート', 'Export as JSON')}
          >
            📤 Export
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.csv,.txt"
        style={{ display: 'none' }}
        onChange={handleFileImport}
      />

      {/* 用語タグ一覧 */}
      <div className="glossary-list">
        {terms.map((term) => (
          <span key={term} className="glossary-tag">
            {term}
            <button
              type="button"
              className="glossary-tag-remove"
              onClick={() => onRemove(term)}
              title={t('削除', 'Remove')}
            >
              ×
            </button>
          </span>
        ))}
        {terms.length === 0 && (
          <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{t('用語がありません', 'No terms yet')}</span>
        )}
      </div>

      {/* 追加フォーム */}
      <div className="glossary-input-row">
        <input
          type="text"
          className="input-text"
          placeholder={t('例：Nu Skin　／　読み替え：New Skin → Nu Skin', 'e.g. Nu Skin  /  replace: New Skin → Nu Skin')}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
        >
          {t('追加', 'Add')}
        </button>
      </div>

      {/* 翻訳に反映 */}
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={onApply}
        style={{ marginTop: '0.5rem', width: '100%' }}
        title={t('用語集を翻訳AIに送信', 'Send glossary to the translator')}
      >
        {t('✅ 翻訳AIに反映', '✅ Apply glossary')}
      </button>
    </div>
  );
}
